import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';

// Initial state
const initialState = {
  comments: [],
  comment: null,
  loading: false,
  error: null
};

// Get comments for a paper
export const getComments = createAsyncThunk(
  'comments/getComments',
  async (paperId, { rejectWithValue }) => {
    try {
      const response = await axios.get(`/api/comments/paper/${paperId}`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Get comment replies
export const getCommentReplies = createAsyncThunk(
  'comments/getCommentReplies',
  async (commentId, { rejectWithValue }) => {
    try {
      const res = await axios.get(`/api/comments/${commentId}/replies`);
      return { commentId, data: res.data };
    } catch (err) {
      return rejectWithValue(
        err.response?.data?.error || 'Error fetching replies'
      );
    }
  }
);

// Add a comment
export const addComment = createAsyncThunk(
  'comments/addComment',
  async (commentData, { rejectWithValue }) => {
    try {
      const response = await axios.post('/api/comments', commentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Update a comment
export const updateComment = createAsyncThunk(
  'comments/updateComment',
  async ({ id, commentData }, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/api/comments/${id}`, commentData);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Delete a comment
export const deleteComment = createAsyncThunk(
  'comments/deleteComment',
  async (commentId, { rejectWithValue }) => {
    try {
      await axios.delete(`/api/comments/${commentId}`);
      return commentId;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Upvote a comment
export const upvoteComment = createAsyncThunk(
  'comments/upvoteComment',
  async (commentId, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/api/comments/${commentId}/upvote`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Downvote a comment
export const downvoteComment = createAsyncThunk(
  'comments/downvoteComment',
  async (commentId, { rejectWithValue }) => {
    try {
      const response = await axios.put(`/api/comments/${commentId}/downvote`);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const commentSlice = createSlice({
  name: 'comments',
  initialState,
  reducers: {
    clearComments: (state) => {
      state.comments = [];
      state.comment = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Get comments
      .addCase(getComments.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getComments.fulfilled, (state, action) => {
        state.loading = false;
        state.comments = action.payload;
      })
      .addCase(getComments.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Get comment replies
      .addCase(getCommentReplies.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCommentReplies.fulfilled, (state, action) => {
        state.loading = false;
        state.replies = {
          ...state.replies,
          [action.payload.commentId]: action.payload.data.data
        };
      })
      .addCase(getCommentReplies.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Add comment
      .addCase(addComment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addComment.fulfilled, (state, action) => {
        state.loading = false;
        state.comments.push(action.payload);
      })
      .addCase(addComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Update comment
      .addCase(updateComment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateComment.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.comments.findIndex(comment => comment._id === action.payload._id);
        if (index !== -1) {
          state.comments[index] = action.payload;
        }
      })
      .addCase(updateComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Delete comment
      .addCase(deleteComment.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteComment.fulfilled, (state, action) => {
        state.loading = false;
        state.comments = state.comments.filter(comment => comment._id !== action.payload);
      })
      .addCase(deleteComment.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      
      // Upvote comment
      .addCase(upvoteComment.fulfilled, (state, action) => {
        const index = state.comments.findIndex(comment => comment._id === action.payload._id);
        if (index !== -1) {
          state.comments[index] = action.payload;
        }
      })
      
      // Downvote comment
      .addCase(downvoteComment.fulfilled, (state, action) => {
        const index = state.comments.findIndex(comment => comment._id === action.payload._id);
        if (index !== -1) {
          state.comments[index] = action.payload;
        }
      });
  }
});

export const { clearComments } = commentSlice.actions;

export default commentSlice.reducer; 